import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { Container, Menu } from 'semantic-ui-react';

import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasRESTClient from '../../hocs/has-rest-client';
import hasOrgansation from '../../hocs/has-organisation';

import CampaignHeader from './campaign-header';
import InfluencersTab from './influencers-tab';
import SettingsTab from './settings-tab';
import ReportsDropdown from './reports-dropdown';
import { MIN_SEARCH_LENGTH, PAGE_SIZE } from './constants';

const JUMBO_TYPE = 'JUMBO';
const SEARCH_DEBOUNCE_MS = 350;
const SETTINGS_TAB = 'settings';

// Page and search live in the URL (page is 1-based) so a view can be shared.
const pageFromProps = props => {
  const raw = parseInt(_.get(props, 'location.query.page', '1'), 10);
  return Number.isNaN(raw) || raw < 1 ? 0 : raw - 1;
};

const searchFromProps = props => _.get(props, 'location.query.q', '');

const tabFromProps = props => _.get(props, 'location.query.tab', '');

class LargeCampaign extends Component {
  constructor(props) {
    super(props);
    this.state = {
      campaign: {},
      creators: [],
      total: null,
      isLoading: true,
      hasFailed: false,
      sort: 'desc',
      searchInput: searchFromProps(props),
    };
  }

  componentDidMount() {
    this.loadCampaign();
    if (tabFromProps(this.props) !== SETTINGS_TAB) this.loadCreators();
  }

  componentWillReceiveProps(nextProps) {
    const pageChanged = pageFromProps(nextProps) !== pageFromProps(this.props);
    const searchChanged = searchFromProps(nextProps) !== searchFromProps(this.props);
    const leftSettings = tabFromProps(this.props) === SETTINGS_TAB
      && tabFromProps(nextProps) !== SETTINGS_TAB;

    if (searchChanged) this.setState({ searchInput: searchFromProps(nextProps) });
    if (pageChanged || searchChanged || leftSettings) this.loadCreators(nextProps);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.pushSearch.cancel();
  }

  onPageChange = page => this.updateQuery({ page: page + 1 });

  onTabChange = tab => this.updateQuery({ tab: tab || undefined });

  // Same target as the collaboration cards elsewhere; :collabId is the discussion id.
  onCreatorSelect = discussionId => {
    const { params: { id: campaignId } } = this.props;
    this.context.router.push({
      pathname: `/campaign/${campaignId}/collaboration/${discussionId}`,
    });
  };

  // Persist the edited fields, then refresh the header from the saved resource.
  onSettingsSave = attributes => {
    const { jsonAPIClient, params: { id: campaignId } } = this.props;
    return jsonAPIClient.get('campaigns', campaignId)
      .then(resource => {
        _.forEach(attributes, (value, key) => {
          if (!_.isNull(value)) resource.set(key, value);
        });
        return resource.sync();
      })
      .then(() => this.loadCampaign());
  };

  // Typing updates the field immediately; the URL (and the fetch) follow on a debounce.
  onSearchChange = (e, { value }) => {
    this.setState({ searchInput: value });
    this.pushSearch(value);
  };

  onSearchClear = () => {
    this.setState({ searchInput: '' });
    this.pushSearch.cancel();
    this.updateQuery({ q: undefined, page: 1 });
  };

  // Toggle last-scanned direction and reload from the first page. Already on page 1 means the
  // URL would not change, so refetch directly rather than waiting on componentWillReceiveProps.
  onSortChange = () => {
    const sort = this.state.sort === 'desc' ? 'asc' : 'desc';
    this.setState({ sort }, () => {
      if (pageFromProps(this.props) === 0) this.loadCreators();
      else this.updateQuery({ page: 1 });
    });
  };

  // A search below the minimum is sent as empty so the API browses rather than filters.
  pushSearch = _.debounce(value => {
    const q = _.size(_.trim(value)) >= MIN_SEARCH_LENGTH ? _.trim(value) : undefined;
    if (q === (searchFromProps(this.props) || undefined)) return;
    this.updateQuery({ q, page: 1 });
  }, SEARCH_DEBOUNCE_MS);

  updateQuery = changes => {
    const { location } = this.props;
    const query = _.omitBy({ ...location.query, ...changes }, _.isUndefined);
    this.context.router.push({ pathname: location.pathname, query });
  };

  setSafely = state => {
    if (!this.isUnmounted) this.setState(state);
  };

  // Gate on the campaign type: only JUMBO campaigns belong on this page.
  loadCampaign = () => {
    const { jsonAPIClient, params: { id: campaignId } } = this.props;
    jsonAPIClient.get('campaigns', campaignId)
      .then(resource => {
        const campaign = resource.toJSONTree();
        if (campaign.campaignType !== JUMBO_TYPE) {
          this.context.router.replace('/');
          return;
        }
        this.setSafely({ campaign });
      })
      .catch(() => this.context.router.replace('/'));
  };

  // One call covers the page, the total and the handle search.
  loadCreators = (props = this.props) => {
    const { campaignAPIClient, params: { id: campaignId } } = props;
    const page = pageFromProps(props) + 1;
    const search = searchFromProps(props);
    const requestId = _.uniqueId();

    this.latestRequestId = requestId;
    this.setState({ isLoading: true, hasFailed: false });

    campaignAPIClient.get(`/jumbo/campaigns/${campaignId}/creators`, {
      params: { page, perPage: PAGE_SIZE, order: this.state.sort, search: search || undefined },
    })
      .then(response => {
        if (this.latestRequestId !== requestId) return; // a newer request won
        const payload = _.get(response, 'data.data', {});
        this.setSafely({
          creators: _.get(payload, 'data', []),
          total: _.get(payload, 'metadata.total', 0),
          isLoading: false,
        });
      })
      .catch(() => {
        if (this.latestRequestId !== requestId) return;
        this.setSafely({ creators: [], total: null, isLoading: false, hasFailed: true });
      });
  };

  render() {
    const { campaign, creators, total, isLoading, hasFailed, sort, searchInput } = this.state;
    const { organisation } = this.props;
    const isSettings = tabFromProps(this.props) === SETTINGS_TAB;

    return (
      <Container style={{ marginTop: '2rem' }}>
        <CampaignHeader campaign={campaign} />

        <Menu pointing secondary>
          <Menu.Item active={!isSettings} onClick={() => this.onTabChange('')}>Influencers</Menu.Item>
          <Menu.Item active={isSettings} onClick={() => this.onTabChange(SETTINGS_TAB)}>Settings</Menu.Item>
          <Menu.Menu position="right">
            <ReportsDropdown campaign={campaign} organisation={organisation} />
          </Menu.Menu>
        </Menu>

        {isSettings
          ? <SettingsTab key={campaign.id || 'loading'} campaign={campaign} onSave={this.onSettingsSave} />
          : <InfluencersTab
            creators={creators}
            page={pageFromProps(this.props)}
            pageSize={PAGE_SIZE}
            total={total}
            isLoading={isLoading}
            hasFailed={hasFailed}
            onPageChange={this.onPageChange}
            sort={sort}
            onSortChange={this.onSortChange}
            searchInput={searchInput}
            searchTerm={searchFromProps(this.props)}
            onSearchChange={this.onSearchChange}
            onSearchClear={this.onSearchClear}
            onCreatorSelect={this.onCreatorSelect}
          />}
      </Container>
    );
  }
}

LargeCampaign.contextTypes = {
  router: PropTypes.object.isRequired,
};

LargeCampaign.propTypes = {
  params: PropTypes.shape({ id: PropTypes.string }).isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string, query: PropTypes.object }).isRequired,
  jsonAPIClient: PropTypes.shape({ get: PropTypes.func }).isRequired,
  campaignAPIClient: PropTypes.shape({ get: PropTypes.func }).isRequired,
  organisation: PropTypes.object,
};

LargeCampaign.defaultProps = {
  organisation: {},
};

export default compose(hasJSONAPIClient, hasRESTClient, hasOrgansation)(LargeCampaign);



/** WEBPACK FOOTER **
 ** ./src/containers/large-campaign/index.js
 **/